import { useState } from "react";
import { Phone, Send, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuoteForm({ services = [], cities = [], departments = [], variant = "default" }) {
  // Generate cities from departments if no cities provided directly
  const availableCities = cities.length > 0 ? cities : 
    departments.flatMap(dept => 
      dept.cities?.map(city => ({ id: city, name: city })) || []
    );
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    city: "",
    service: "",
    urgency: "normal"
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        toast.success("Demande envoyée ! Nous vous rappelons rapidement.");
        setFormData({ name: "", phone: "", city: "", service: "", urgency: "normal" });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } catch (error) {
      toast.error("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`bg-white rounded-2xl shadow-2xl p-8 ${variant === "hero" ? "border-2 border-green-500" : ""}`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-[#0f2744] mb-2">
            Demande reçue !
          </h3>
          <p className="text-gray-600 mb-4">
            Un technicien vous rappellera dans les plus brefs délais.
          </p>
          <p className="text-sm text-gray-500">
            En cas d'urgence, appelez-nous directement.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-white rounded-2xl shadow-2xl p-6 md:p-8"
      data-testid="quote-form"
    >
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-[#0f2744] mb-2">
          Demande de devis rapide
        </h3>
        <p className="text-gray-600 text-sm">
          Réponse en moins de 15 minutes
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom complet *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Votre nom"
            required
            className="w-full"
            data-testid="quote-name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Téléphone *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="06 12 34 56 78"
            required
            className="w-full"
            data-testid="quote-phone"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ville
          </label>
          <select
            name="city"
            value={formData.city}
            onChange={handleChange}
            className="w-full"
            data-testid="quote-city"
          >
            <option value="">Sélectionnez votre ville</option>
            {availableCities.map((city) => (
              <option key={city.id || city.name} value={city.name}>
                {city.name}
              </option>
            ))}
            <option value="Autre">Autre ville</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type d'intervention
          </label>
          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full"
            data-testid="quote-service"
          >
            <option value="">Sélectionnez un service</option>
            {services.map((service) => (
              <option key={service.id} value={service.name}>
                {service.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Urgence
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label 
              className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                formData.urgency === "normal" 
                  ? "border-blue-500 bg-blue-50 text-blue-700" 
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="urgency"
                value="normal"
                checked={formData.urgency === "normal"}
                onChange={handleChange}
                className="sr-only"
              />
              <span className="font-medium">Normal</span>
            </label>
            <label 
              className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                formData.urgency === "urgent" 
                  ? "border-red-500 bg-red-50 text-red-700" 
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="urgency"
                value="urgent"
                checked={formData.urgency === "urgent"}
                onChange={handleChange}
                className="sr-only"
              />
              <span className="font-medium">Urgent</span>
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-cta w-full flex items-center justify-center gap-2"
          data-testid="quote-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Envoyer ma demande
            </>
          )}
        </button>
      </form>

      <div className="mt-6 pt-6 border-t border-gray-200 text-center">
        <p className="text-base font-bold text-orange-600 mb-2">
          Besoin d'un serrurier Fichet ?
        </p>
        <a
          href="tel:+33972574450"
          onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion('tel:+33972574450'); }}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg"
          data-testid="quote-phone-link"
        >
          <Phone className="w-5 h-5" />
          09 72 57 44 50
        </a>
      </div>
    </div>
  );
}
